// CreateRoomPage — Phase 6 Task 6 「1秒作成 UX」.
//
// 設定ページ (確定方針 V — 4 項目):
//   1. ルーム名 (任意, 空欄 OK)
//   2. canvas size (旧版4種から radio 選択)
//   3. 「オーナーパスワードを設定する」CB → 8〜128 文字の input が出現
//   4. 「email を登録する」CB → email input が出現
//
// 「作成」押下:
//   1. validateForm でクライアント側 validation
//   2. ok なら DispatcherClient.createRoom 呼び出し
//   3. ok 応答なら adminToken を localStorage に保存 (確定方針 T)
//   4. URL を pathname `/<roomId>` に置換 (history.pushState で popstate 発火)
//      → ReJoinScreen が画面に出る (display name 入力 → 入室)
//   5. emailVerificationSent=false で email CB ON だった場合は warning 表示
//      (Phase 6 Task 6.5 確定方針 U: server は 200 + false を返すように変更済)
//
// **戻るボタン**: HomePage が `/create` を pushState しているので、
// ブラウザ戻るでキャンセル可 (確定方針 S).
//
// Phase 6 Task 6.6-B: HomePage のメイン CTA は「ワンクリック作成」に変更され、
// 本ページは HomePage のサブリンク「詳細設定で作成」から到達する逃げ道となる.
// 構造自体は変更なし.

import { useState } from 'react';

import {
  CANVAS_DIMENSION_MAX,
  CANVAS_DIMENSION_MIN,
  CANVAS_SIZE_PRESETS,
  type CanvasSizePreset,
  type CreateRoomFailureMessage,
  type CreateRoomFormValues,
  ENTRY_PASSWORD_MAX_LENGTH,
  type FormFieldError,
  fieldErrorText,
  INITIAL_FORM_VALUES,
  MAX_ROOM_NAME_LENGTH,
  messageForFailure,
  OWNER_EMAIL_MAX_LENGTH,
  OWNER_PASSWORD_MAX_LENGTH,
  OWNER_PASSWORD_MIN_LENGTH,
  partialEmailFailureMessage,
  validateForm,
} from '../application/createRoomForm.js';
import {
  ENTRY_SHOW_MEMBERS_FIELD,
  ENTRY_SHOW_PREVIEW_FIELD,
} from '../application/entryVisibilityFields.js';
import { AlphaDataWarningNotice } from './AlphaWarningBanner.js';
import { EntryVisibilityCheckbox } from './EntryVisibilityCheckbox.js';
import { useSession } from './hooks/useSession.js';
import { navigateToHome, navigateToRoom } from './urlNavigation.js';

type Status =
  | { readonly t: 'idle' }
  | { readonly t: 'creating' }
  | { readonly t: 'failed'; readonly message: CreateRoomFailureMessage };

const goHome = (): void => {
  navigateToHome();
};

// pushState (replaceState ではない) — HomePage → CreateRoomPage の戻る経路を
// temporarily スタックに残す. ユーザが「ルーム作成成功」直後に戻るボタンで
// CreateRoomPage に戻れるのは、この時点でルームが既にある以上やや混乱するが、
// ここでは "戻るで HomePage に戻れる" 方が UX 上有益.
const goToRoom = (roomId: string): void => {
  navigateToRoom(roomId);
};

export const CreateRoomPage = (): React.ReactNode => {
  const { dispatcher, adminTokens } = useSession();
  const [values, setValues] = useState<CreateRoomFormValues>(INITIAL_FORM_VALUES);
  const [fieldErrors, setFieldErrors] = useState<
    Partial<Record<keyof CreateRoomFormValues, FormFieldError>>
  >({});
  const [status, setStatus] = useState<Status>({ t: 'idle' });

  const update = <K extends keyof CreateRoomFormValues>(
    key: K,
    val: CreateRoomFormValues[K],
  ): void => {
    setValues((prev) => ({ ...prev, [key]: val }));
    if (fieldErrors[key] !== undefined) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (status.t === 'creating') return;

    const v = validateForm(values);
    if (v.outcome === 'failed') {
      setFieldErrors(v.errors);
      return;
    }

    setFieldErrors({});
    setStatus({ t: 'creating' });

    const out = await dispatcher.createRoom(v.input);

    if (out.outcome !== 'ok') {
      setStatus({ t: 'failed', message: messageForFailure(out) });
      return;
    }

    // ─── ok ────────────────────────────────────────────────────────────────
    // 1. admin_token は localStorage 専用 (確定方針 T、URL に乗せない).
    adminTokens.save(out.roomId, out.adminToken);

    // 2. Phase 6 Task 6.5 確定方針 U: email を要求した CB ON だが server が
    //    `emailVerificationSent: false` を返したケース (送信失敗). room は
    //    作成済 + admin_token も受領済なので、warning を出して遷移を続ける.
    //    再送は Task 8 管理画面 (Task 6.6-B 入室後設定モーダルの email 欄
    //    再送経由) で行う.
    if (values.ownerEmailEnabled && !out.emailVerificationSent) {
      setStatus({ t: 'failed', message: partialEmailFailureMessage() });
      // それでも遷移は続けたい (room 自体は成功している).
    }

    // 3. URL を pathname `/<id>` に切り替え → ReJoinScreen が出る.
    goToRoom(out.roomId);
  };

  const isCreating = status.t === 'creating';

  return (
    // px-4 + w-full max-w: 固定 w-[480px] はスマホ縦 (390/360px) で viewport を超える
    // (2026-07-03 実機 fb、ErrorScreen と同パターン)。縦は form の my-6 が担うので px のみ。
    <div className="grid h-full place-items-center overflow-y-auto px-4">
      <form
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        className="my-6 w-full max-w-[480px] rounded border border-white/10 bg-[var(--color-panel)] p-6 shadow-xl"
        aria-label="ルーム設定フォーム"
      >
        <h1 className="mb-1 text-center text-lg font-bold tracking-wide text-[var(--color-accent)]">
          ルームを作成
        </h1>
        <p className="mb-5 text-center text-xs text-[var(--color-text-muted)]">
          1秒で作成・即入室。永続化は任意 (後で管理画面から追加できます)
        </p>

        {/* ─── 1. ルーム名 ─────────────────────────────────────────────── */}
        <label className="mb-4 block text-sm">
          ルーム名 (空欄なら「お絵描きルーム」)
          <input
            type="text"
            value={values.roomName}
            onChange={(e) => update('roomName', e.target.value)}
            maxLength={MAX_ROOM_NAME_LENGTH}
            disabled={isCreating}
            className="mt-1 w-full rounded border border-white/30 bg-[var(--color-bg-soft)] px-2 py-1 text-base text-white outline-none focus:border-[var(--color-accent)] disabled:opacity-50"
            placeholder="お絵描きルーム"
          />
          {fieldErrors.roomName !== undefined && (
            <span className="mt-1 block text-xs text-[var(--color-error)]">
              {fieldErrorText(fieldErrors.roomName)}
            </span>
          )}
        </label>

        {/* ─── あいことば (任意、owner-vs-entry-password-2026-06-13.md) ───
            オーナーパスワード (下の折りたたみセクション) とは別物・別枠に
            置く (「2 つのパスワード欄を並べない」確定方針)。入室するゲストが
            打つ合言葉で、チェックボックスなしの常時表示欄 (空欄 = 未設定)。
            ラベルの「（パスワード）」は 2026-09-19 追記 (結論 1 の部分改定、
            作者判断) — 旧版ユーザーの「パスワード」語彙に寄せた理解補助。 */}
        <label className="mb-1 block text-sm">
          あいことば（パスワード） (任意 — 入室するゲストが打つ合言葉)
          <input
            type="text"
            value={values.entryPassword}
            onChange={(e) => update('entryPassword', e.target.value)}
            maxLength={ENTRY_PASSWORD_MAX_LENGTH}
            disabled={isCreating}
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            className="mt-1 w-full rounded border border-white/30 bg-[var(--color-bg-soft)] px-2 py-1 text-base text-white outline-none focus:border-[var(--color-accent)] disabled:opacity-50"
            placeholder="空欄なら誰でも入室できます"
          />
        </label>
        {/* label の外 (兄弟要素) に置く — 内側に置くと label の accessible name
            に混ざり `getByLabelText('あいことば（パスワード） (…)')` の完全一致
            マッチが壊れる (entryPasswordError の配置と同じ流儀、ReJoinScreen 参照). */}
        <p className="mb-4 text-xs text-[var(--color-text-muted)]">ひらがなや漢字も使えます</p>
        {fieldErrors.entryPassword !== undefined && (
          <p className="-mt-2 mb-4 text-xs text-[var(--color-error)]">
            {fieldErrorText(fieldErrors.entryPassword)}
          </p>
        )}

        {/* ─── 2. canvas size ─────────────────────────────────────────── */}
        {/*
         * 2026-05 改訂: 旧 4 種 (小〜大、aspect ratio バラバラ) を廃し、
         * 正方形 3 種 (標準 1000² / 大 1500² / 特大 2000²) + カスタム入力
         * (500〜4000 / 整数) に再構成. 4 列 grid のまま 4 番目を「カスタム」
         * にし、選択時に下に width/height 入力フィールドを表示する.
         */}
        <fieldset className="mb-4 text-sm">
          <legend className="mb-1">キャンバスサイズ</legend>
          <div className="grid grid-cols-4 gap-1">
            {CANVAS_SIZE_PRESETS.map((preset: CanvasSizePreset) => {
              const checked = values.canvasSizeId === preset.id;
              return (
                <label
                  key={preset.id}
                  className={`flex cursor-pointer flex-col items-center rounded border px-2 py-2 text-center text-xs transition ${
                    checked
                      ? 'border-[var(--color-accent)] bg-[var(--color-bg-soft)]'
                      : 'border-white/20 hover:bg-[var(--color-bg-soft)]/60'
                  }`}
                >
                  <input
                    type="radio"
                    name="canvasSize"
                    value={preset.id}
                    checked={checked}
                    onChange={() => update('canvasSizeId', preset.id)}
                    disabled={isCreating}
                    className="sr-only"
                  />
                  <span className="font-bold">{preset.label}</span>
                  <span className="text-[11px] text-[var(--color-text-muted)]">
                    {preset.width}×{preset.height}
                  </span>
                </label>
              );
            })}
            {(() => {
              const checked = values.canvasSizeId === 'custom';
              return (
                <label
                  className={`flex cursor-pointer flex-col items-center rounded border px-2 py-2 text-center text-xs transition ${
                    checked
                      ? 'border-[var(--color-accent)] bg-[var(--color-bg-soft)]'
                      : 'border-white/20 hover:bg-[var(--color-bg-soft)]/60'
                  }`}
                >
                  <input
                    type="radio"
                    name="canvasSize"
                    value="custom"
                    checked={checked}
                    onChange={() => update('canvasSizeId', 'custom')}
                    disabled={isCreating}
                    className="sr-only"
                  />
                  <span className="font-bold">カスタム</span>
                  <span className="text-[11px] text-[var(--color-text-muted)]">
                    {checked
                      ? `${values.customCanvasWidth}×${values.customCanvasHeight}`
                      : '自由入力'}
                  </span>
                </label>
              );
            })()}
          </div>
          {values.canvasSizeId === 'custom' && (
            <div className="mt-2 grid grid-cols-2 gap-2">
              <label className="block">
                <span className="block text-xs text-[var(--color-text-muted)]">
                  幅 (px、{CANVAS_DIMENSION_MIN}〜{CANVAS_DIMENSION_MAX})
                </span>
                <input
                  type="number"
                  inputMode="numeric"
                  step={1}
                  min={CANVAS_DIMENSION_MIN}
                  max={CANVAS_DIMENSION_MAX}
                  value={values.customCanvasWidth}
                  onChange={(e) => {
                    // Empty-string → NaN → validateCustomCanvasDimension で
                    // 'not-integer' に倒れる. State 上は number 型を維持
                    // (string にすると CreateRoomFormValues 型が複雑化する).
                    const raw = e.target.value;
                    const n = raw === '' ? Number.NaN : Number(raw);
                    update('customCanvasWidth', n);
                  }}
                  disabled={isCreating}
                  placeholder="1000"
                  className="mt-1 w-full rounded border border-white/30 bg-[var(--color-bg-soft)] px-2 py-1 text-base text-white outline-none focus:border-[var(--color-accent)] disabled:opacity-50"
                  aria-label="キャンバス幅"
                />
                {fieldErrors.customCanvasWidth !== undefined && (
                  <span className="mt-1 block text-xs text-[var(--color-error)]">
                    {fieldErrorText(fieldErrors.customCanvasWidth)}
                  </span>
                )}
              </label>
              <label className="block">
                <span className="block text-xs text-[var(--color-text-muted)]">
                  高さ (px、{CANVAS_DIMENSION_MIN}〜{CANVAS_DIMENSION_MAX})
                </span>
                <input
                  type="number"
                  inputMode="numeric"
                  step={1}
                  min={CANVAS_DIMENSION_MIN}
                  max={CANVAS_DIMENSION_MAX}
                  value={values.customCanvasHeight}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const n = raw === '' ? Number.NaN : Number(raw);
                    update('customCanvasHeight', n);
                  }}
                  disabled={isCreating}
                  placeholder="1000"
                  className="mt-1 w-full rounded border border-white/30 bg-[var(--color-bg-soft)] px-2 py-1 text-base text-white outline-none focus:border-[var(--color-accent)] disabled:opacity-50"
                  aria-label="キャンバス高さ"
                />
                {fieldErrors.customCanvasHeight !== undefined && (
                  <span className="mt-1 block text-xs text-[var(--color-error)]">
                    {fieldErrorText(fieldErrors.customCanvasHeight)}
                  </span>
                )}
              </label>
            </div>
          )}
        </fieldset>

        {/* ─── 3. オーナーパスワード CB ───────────────────────────────── */}
        <label className="mb-2 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={values.ownerPasswordEnabled}
            onChange={(e) => update('ownerPasswordEnabled', e.target.checked)}
            disabled={isCreating}
          />
          オーナーパスワードを設定する (任意 — 別端末からのオーナー認証用)
        </label>
        {values.ownerPasswordEnabled && (
          <label className="mb-4 block text-sm">
            <input
              type="password"
              value={values.ownerPassword}
              onChange={(e) => update('ownerPassword', e.target.value)}
              minLength={OWNER_PASSWORD_MIN_LENGTH}
              maxLength={OWNER_PASSWORD_MAX_LENGTH}
              disabled={isCreating}
              autoComplete="new-password"
              className="mt-1 w-full rounded border border-white/30 bg-[var(--color-bg-soft)] px-2 py-1 text-base text-white outline-none focus:border-[var(--color-accent)] disabled:opacity-50"
              placeholder={`${OWNER_PASSWORD_MIN_LENGTH}〜${OWNER_PASSWORD_MAX_LENGTH} 文字`}
            />
            {fieldErrors.ownerPassword !== undefined && (
              <span className="mt-1 block text-xs text-[var(--color-error)]">
                {fieldErrorText(fieldErrors.ownerPassword)}
              </span>
            )}
          </label>
        )}

        {/* ─── 4. email CB ───────────────────────────────────────────── */}
        <label className="mb-2 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={values.ownerEmailEnabled}
            onChange={(e) => update('ownerEmailEnabled', e.target.checked)}
            disabled={isCreating}
          />
          email を登録する (任意 — 次回以降このメールアドレスで再ログイン)
        </label>
        {values.ownerEmailEnabled && (
          <label className="mb-4 block text-sm">
            <input
              type="email"
              value={values.ownerEmail}
              onChange={(e) => update('ownerEmail', e.target.value)}
              maxLength={OWNER_EMAIL_MAX_LENGTH}
              disabled={isCreating}
              autoComplete="email"
              spellCheck={false}
              className="mt-1 w-full rounded border border-white/30 bg-[var(--color-bg-soft)] px-2 py-1 text-base text-white outline-none focus:border-[var(--color-accent)] disabled:opacity-50"
              placeholder="owner@example.com"
            />
            {fieldErrors.ownerEmail !== undefined && (
              <span className="mt-1 block text-xs text-[var(--color-error)]">
                {fieldErrorText(fieldErrors.ownerEmail)}
              </span>
            )}
          </label>
        )}

        {/* ─── 5. 入室画面の表示 (entry-preview-members-2026-07-21 案 A) ──
            文言・デフォルト (On) は entryVisibilityFields / 共有 CB 由来 =
            RoomSettingsModal と同一知識. Off のときだけ POST body に false が
            載る (On はキー省略、createRoomForm 側で組み立て). */}
        <fieldset className="mb-2 text-sm">
          <legend className="mb-1">入室画面の表示</legend>
          <div className="flex flex-col gap-2">
            <EntryVisibilityCheckbox
              spec={ENTRY_SHOW_PREVIEW_FIELD}
              checked={values.entryShowPreview}
              disabled={isCreating}
              onChange={(next) => update('entryShowPreview', next)}
            />
            <EntryVisibilityCheckbox
              spec={ENTRY_SHOW_MEMBERS_FIELD}
              checked={values.entryShowMembers}
              disabled={isCreating}
              onChange={(next) => update('entryShowMembers', next)}
            />
          </div>
        </fieldset>

        {/* ─── α 期データ削除警告 (2026-05-23 W2 公開向け) ────────────────
            「作成」ボタンの直上に置いてユーザが submit 前に気付ける位置にする. */}
        <div className="mt-3 mb-2">
          <AlphaDataWarningNotice />
        </div>

        {/* ─── Buttons ─────────────────────────────────────────────────── */}
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            onClick={goHome}
            disabled={isCreating}
            className="flex-1 rounded border border-white/30 bg-[var(--color-bg-soft)] px-4 py-2 text-base text-white transition hover:bg-[var(--color-bg)] disabled:opacity-50"
          >
            キャンセル
          </button>
          <button
            type="submit"
            disabled={isCreating}
            className="flex-1 rounded bg-[var(--color-accent)] px-4 py-2 text-base font-bold text-[var(--color-panel)] transition hover:brightness-110 disabled:opacity-60"
          >
            {isCreating ? '作成中...' : '作成'}
          </button>
        </div>

        {status.t === 'failed' && (
          <p
            className={`mt-3 text-center text-sm ${
              status.message.kind === 'partial'
                ? 'text-[var(--color-accent-soft)]'
                : 'text-[var(--color-error)]'
            }`}
            role="alert"
          >
            {status.message.message}
            {status.message.detail !== undefined && (
              <span className="mt-1 block font-mono text-xs text-[var(--color-text-muted)]">
                {status.message.detail}
              </span>
            )}
          </p>
        )}
      </form>
    </div>
  );
};
